import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { Send, MapPin, Mail, Phone, CheckCircle, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { trackContactFormSubmission } from '@/hooks/useGoogleAnalytics';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { generalContact, decodeContactInfo } from '@/data/contacts';

// Turnstile Site Key from environment
const TURNSTILE_SITE_KEY =
  typeof import.meta.env.VITE_TURNSTILE_SITE_KEY === 'string'
    ? import.meta.env.VITE_TURNSTILE_SITE_KEY
    : '';

const SERVICE_OPTIONS = [
  { id: 'ki-beratung', label: 'KI-Beratung' },
  { id: 'ki-kompetenz-schulung', label: 'KI-Kompetenz-Schulung' },
  { id: 'neue-webseite', label: 'Neue Webseite' },
  { id: 'webseiten-redesign', label: 'Webseiten-Redesign' },
  { id: 'seo-optimierung', label: 'SEO-Optimierung' },
  { id: 'hosting-domains', label: 'Hosting & Domains' },
  { id: 'buchungssystem', label: 'Buchungssystem' },
  { id: 'onlineshop', label: 'Onlineshop' },
  { id: 'beratung', label: 'Beratung' },
  { id: 'sonstiges', label: 'Sonstiges' },
];


declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const consent = useCookieConsent();
  const [shouldLoadTurnstile, setShouldLoadTurnstile] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
  }>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  useEffect(() => {
    if (consent === 'accepted') {
      setShouldLoadTurnstile(true);
    }
  }, [consent]);

  const handleTurnstileActivate = () => {
    if (!shouldLoadTurnstile) {
      setShouldLoadTurnstile(true);
    }
  };

  // Load Turnstile script and render widget
  useEffect(() => {
    if (!shouldLoadTurnstile) return;
    if (!TURNSTILE_SITE_KEY) {
      console.warn('Turnstile Site Key not configured');
      return;
    }

    const loadTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError(false);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': () => {
            setTurnstileError(true);
            setTurnstileToken(null);
          },
          theme: 'light',
          size: 'normal',
        });
      }
    };

    // Check if script already loaded
    if (window.turnstile) {
      loadTurnstile();
    } else {
      // Load Turnstile script
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => {
        // Small delay to ensure turnstile is initialized
        setTimeout(loadTurnstile, 100);
      };
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [shouldLoadTurnstile]);

  const resetTurnstile = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Bitte geben Sie Ihren Namen ein.';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Bitte geben Sie eine Nachricht ein.';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form fields first
    if (!validateForm()) {
      return;
    }
    
    if (!turnstileToken) {
      toast({
        title: "Captcha erforderlich",
        description: "Bitte bestätigen Sie, dass Sie kein Roboter sind.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...formData,
          services: selectedServices.map(id => SERVICE_OPTIONS.find(s => s.id === id)?.label || id),
          turnstileToken,
        },
      });

      if (error) throw error;
      
      if (data && !data.success) {
        throw new Error(data.error || 'Captcha-Verifizierung fehlgeschlagen');
      }

      setIsSubmitted(true);
      
      // Track successful form submission in Google Analytics
      trackContactFormSubmission('homepage_contact');
      
      toast({
        title: "Anfrage gesendet!",
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      resetTurnstile();
      toast({
        title: "Fehler beim Senden",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleServiceToggle = (serviceId: string, checked: boolean) => {
    setSelectedServices(prev => 
      checked 
        ? [...prev, serviceId]
        : prev.filter(id => id !== serviceId)
    );
  };

  return (
    <section id="kontakt" className="py-24 md:py-32 relative section-dark">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Kontakt</span>
            <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-white">
              Wie erreichen Sie uns für Ihr
              <span className="gradient-text"> Projekt?</span>
            </h2>
            <p className="text-white/80 text-lg mb-10">
              Egal ob Sie eine neue Webseite benötigen, Ihre bestehende optimieren möchten 
              oder Beratung zur Digitalisierung suchen – wir sind für Sie da.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-white">Postalische Adresse</h3>
                  <p className="text-white/80">
                    Goldhammer & Kerwel GbR<br />
                    Am Jägerkopf 4<br />
                    38855 Wernigerode
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-white">E-Mail</h3>
                  <a
                    href={`mailto:${decodeContactInfo(generalContact.email)}`}
                    className="text-white/80 hover:text-primary transition-colors"
                  >
                    {decodeContactInfo(generalContact.email)}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-white">Telefon</h3>
                  <a href={`tel:${decodeContactInfo(generalContact.phone)}`} className="text-white/80 hover:text-primary transition-colors">
                    {decodeContactInfo(generalContact.phone).replace(/^\+49/, '0')}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-card rounded-2xl p-6 md:p-8 shadow-xl border border-border">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle className="w-10 h-10 text-primary" aria-hidden="true" />
                  </motion.div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-card-foreground">Vielen Dank!</h3>
                  <p className="text-muted-foreground">
                    Ihre Anfrage ist bei uns eingegangen. Wir melden uns in Kürze bei Ihnen.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  onFocusCapture={handleTurnstileActivate}
                  onPointerDown={handleTurnstileActivate}
                  className="space-y-6"
                  noValidate
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2 text-card-foreground">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        aria-invalid={!!formErrors.name}
                        aria-describedby={formErrors.name ? 'contact-name-error' : undefined}
                        className="w-full px-4 py-3 rounded-lg bg-muted border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-foreground invalid:border-destructive"
                        placeholder="Max Mustermann"
                      />
                      {formErrors.name && (
                        <p id="contact-name-error" role="alert" className="text-sm text-destructive mt-1">
                          {formErrors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-card-foreground">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        aria-invalid={!!formErrors.email}
                        aria-describedby={formErrors.email ? 'contact-email-error' : undefined}
                        className="w-full px-4 py-3 rounded-lg bg-muted border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-foreground invalid:border-destructive"
                        placeholder="max@beispiel.de"
                      />
                      {formErrors.email && (
                        <p id="contact-email-error" role="alert" className="text-sm text-destructive mt-1">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2 text-card-foreground">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-muted border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-foreground"
                        placeholder="+49 123 456789"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium mb-2 text-card-foreground">
                        Unternehmen
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-muted border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-foreground"
                        placeholder="Firma GmbH"
                      />
                    </div>
                  </div>

                  <fieldset>
                    <legend className="block text-sm font-medium mb-3 text-card-foreground">
                      Gewünschte Leistungen (Mehrfachauswahl möglich)
                    </legend>
                    <div className="grid grid-cols-2 gap-3">
                      {SERVICE_OPTIONS.map((service) => (
                        <div key={service.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`service-${service.id}`}
                            checked={selectedServices.includes(service.id)}
                            onCheckedChange={(checked) => handleServiceToggle(service.id, checked as boolean)}
                          />
                          <label
                            htmlFor={`service-${service.id}`}
                            className="text-sm text-card-foreground cursor-pointer"
                          >
                            {service.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2 text-card-foreground">
                      Ihre Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      aria-invalid={!!formErrors.message}
                      aria-describedby={formErrors.message ? 'contact-message-error' : undefined}
                      className="w-full px-4 py-3 rounded-lg bg-muted border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-foreground invalid:border-destructive"
                      placeholder="Beschreiben Sie kurz Ihr Projekt oder Ihre Anforderungen..."
                    />
                    {formErrors.message && (
                      <p id="contact-message-error" role="alert" className="text-sm text-destructive mt-1">
                        {formErrors.message}
                      </p>
                    )}
                  </div>

                  {/* Turnstile CAPTCHA Widget */}
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ShieldCheck className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Spam-Schutz</span>
                    </div>
                    <div 
                      ref={turnstileRef} 
                      className="cf-turnstile"
                    />
                    {!shouldLoadTurnstile && TURNSTILE_SITE_KEY && (
                      <p className="text-xs text-muted-foreground text-center">
                        Captcha wird nach Ihrer ersten Interaktion geladen.
                      </p>
                    )}
                    {turnstileError && (
                      <p className="text-sm text-destructive">
                        Captcha konnte nicht geladen werden. Bitte aktualisieren Sie die Seite.
                      </p>
                    )}
                    {!TURNSTILE_SITE_KEY && (
                      <p className="text-sm text-amber-500">
                        Captcha nicht konfiguriert
                      </p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting || !turnstileToken}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold text-lg hover-glow shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    whileHover={{ scale: turnstileToken ? 1.02 : 1 }}
                    whileTap={{ scale: turnstileToken ? 0.98 : 1 }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Anfrage senden
                        <Send className="w-5 h-5" aria-hidden="true" />
                      </>
                    )}
                  </motion.button>

                  <p className="text-xs text-muted-foreground text-center">
                    Mit dem Absenden erklären Sie sich mit unserer Datenschutzerklärung einverstanden.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
